import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAuditNames,
  fetchGovernanceAudit,
  fetchGovernanceAuditPage,
  type AuditNames,
  type GovernanceAuditEntry,
} from "@/lib/api/organizations";

const distinct = (values: unknown[]): string[] => [
  ...new Set(
    values.filter((v): v is string => typeof v === "string" && v !== ""),
  ),
];

export interface AuditFeed {
  entries: GovernanceAuditEntry[];
  names: AuditNames;
  /// `null` until the first load settles, then `false` when this caller may not
  /// read the log — the section hides rather than showing an error.
  available: boolean | null;
  hasMore: boolean;
  loadingMore: boolean;
  error: string | null;
  loadMore: () => void;
}

/// One page of governance events, plus paging. `spaceId` narrows to a single
/// space; omit it for the whole org. A page lands together with the names of
/// what it mentions, so no row flashes "unknown" before they arrive.
export function useGovernanceAudit(
  orgId: string | null,
  spaceId?: string,
  pageSize = 100,
  admin = false,
): AuditFeed {
  const [entries, setEntries] = useState<GovernanceAuditEntry[]>([]);
  const [names, setNames] = useState<AuditNames>({ models: {}, spaces: {} });
  const asked = useRef(new Set<string>());

  /// Asks only for ids no earlier page asked for.
  const namesFor = useCallback(
    (org: string, page: GovernanceAuditEntry[]): Promise<AuditNames> => {
      const unasked = (ids: string[]) =>
        ids.filter((id) => !asked.current.has(id));
      const models = unasked(distinct(page.map((e) => e.detail?.["model_id"])));
      const spaces = unasked(
        distinct(
          page.flatMap((e) => [
            e.space_id,
            e.detail?.["from_space_id"],
            e.detail?.["with"],
          ]),
        ),
      );
      [...models, ...spaces].forEach((id) => asked.current.add(id));
      return fetchAuditNames(org, { models, spaces }, admin);
    },
    [admin],
  );
  const [cursor, setCursor] = useState<number | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setAvailable(false);
      return;
    }
    let live = true;
    setAvailable(null);
    setError(null);
    asked.current = new Set();
    void fetchGovernanceAudit(orgId, { spaceId, limit: pageSize, admin }).then(
      async (page) => {
        const found = await namesFor(orgId, page?.entries ?? []);
        if (!live) return;
        setNames(found);
        setEntries(page?.entries ?? []);
        setCursor(page?.next_cursor ?? null);
        setAvailable(page !== null);
      },
    );
    return () => {
      live = false;
    };
  }, [orgId, spaceId, pageSize, admin, namesFor]);

  const loadMore = useCallback(() => {
    if (!orgId || cursor === null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    fetchGovernanceAuditPage(orgId, {
      spaceId,
      before: cursor,
      limit: pageSize,
      admin,
    })
      .then(async (page) => {
        const found = await namesFor(orgId, page.entries);
        setNames((prev) => ({
          models: { ...prev.models, ...found.models },
          spaces: { ...prev.spaces, ...found.spaces },
        }));
        // Keyset paging cannot overlap, but a re-fetch of the same cursor
        // could — dedupe so it stays harmless.
        setEntries((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          return [...prev, ...page.entries.filter((e) => !seen.has(e.id))];
        });
        setCursor(page.next_cursor);
      })
      .catch(() => setError("Could not load more events. Try again."))
      .finally(() => setLoadingMore(false));
  }, [orgId, spaceId, cursor, loadingMore, pageSize, admin, namesFor]);

  return {
    entries,
    names,
    available,
    hasMore: cursor !== null,
    loadingMore,
    error,
    loadMore,
  };
}
